import { Button, FlexWrapper, FontIcon, MovieDetails, RentTimer, RewardLabel, Separator } from '@intigral/shared';
import { FlexChild } from '@intigral/shared/components/FlexWrapper';
import { MovieButtons } from '@intigral/ui';
import AddReminderButton from '../../../components/elements/AddReminderButton';
import LikeDislikeButton from '../../../components/elements/LikeDislikeButton';
import PlayVideoButton from '../../../components/elements/PlayVideoButton';
import { UpgradedBanner } from '../../../components/ui/UpgradedBanner';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import { useReminderItem } from '../../../hooks/useReminderItem';
import { ButtonTypes, COMPONENT_TYPES, PROVIDER } from '../../../config/constants';
import { VOD } from '../constants';
import { __ } from '../../../translations';
import useTimeToExpire from '../../../hooks/useTimeToExpire';
import { useRentButtonTitle } from '../hooks/useRentButtonTitle';
import { PlayTrailer } from './elements';
import { useRef } from 'react';
import { useMediaQuery } from '@intigral/hooks';
import h from '../../../helpers/common';
import f2 from '../../../helpers/formatter2';
import { isGamificationEnabled } from '../../gamification/utils/GamificationUtils';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../../interfaces/StoreTypes';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { iDetailOverlayContentProps } from '../types/iDetailOverlay';
import { useSeriesNextEpisode } from '../../../hooks/useSeriesNextEpisode';
import { getUserInfo } from '../../../utils/user';
import { showGuestPromotionPopup, isGuestUser } from '../../../config/guestMode';
import user from '../../../helpers/user';
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';

export const DetailOverlayContent: React.FC<iDetailOverlayContentProps> = ({
    isTvod,
    isLocked,
    isRented,
    isAdded,
    isMuted,
    isLoading,
    hasTrailerUrl,
    showTrailer,
    item,
    tvodInfo,
    expiryDate,
    runtimeLength,
    promotionProviderImages,
    handleGenerateOtpApi,
    handleToggleWatchlistItem,
    toggleWatchlistMutation,
    handleShowTrailer,
    closeTrailerPlayer,
    setShowTrailer,
    handleMute,
}) => {
    // refs and constants
    const playerContainerRef = useRef(null);
    const DetailsStyle = withResponsiveStyles(MovieDetails);
    const componentName = VOD;
    const componentType = COMPONENT_TYPES.VOD_CAROUSEL;
    const dispatch = useDispatch();
    const profile = useSelector((store: IStore) => store.profile);
    const isGuest = isGuestUser(getUserInfo());
    // hooks
    const { isComingSoonItem } = useReminderItem(item);
    const { hours, minutes } = useTimeToExpire(expiryDate);
    const tvodRentNowBtnText = useRentButtonTitle(tvodInfo);
    const gamificationConfig = isGamificationEnabled();
    const { upgraded_banner_fields } = useConfigurationSections();
    const isMobile = useMediaQuery('(max-width: 924px)');
    const {
        title,
        nextEpisode,
        bookmarkData: { isBookmared, percentageNumber },
    } = useSeriesNextEpisode(item);

    const handleLockedPlayClick = (e: React.MouseEvent) => {
        if (showGuestPromotionPopup(e)) {
            return;
        }
        dispatch(showUpgradedModal(true));
    };

    return (
        <FlexWrapper
            flexDirection="column"
            gap="2x"
            paddingTop={{ default: '2x', md: '3x' }}
            paddingBottom={{ default: '1x', md: '2x' }}
        >
            <FlexWrapper
                flexDirection="row"
                justifyContent="between"
                alignItems="center"
                paddingBottom={{ default: '2x', md: '3x' }}
            >
                <MovieButtons
                    reminderButton={
                        isComingSoonItem && <AddReminderButton item={item} category="VOD" isComingSoon={true} />
                    }
                    play={
                        isLocked && !isTvod && !isComingSoonItem && item.programType === 'series' ? (
                            <Button
                                loading={title ? false : true}
                                onClick={handleLockedPlayClick}
                                data-test-id={isBookmared ? 'resume-watching-button' : 'play-now-button'}
                            >
                                {title}
                                <FontIcon name="lock" />
                            </Button>
                        ) : isBookmared && !isComingSoonItem && item.programType === 'series' ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.VodeItem}
                                item={nextEpisode || item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="resume-watching-button"
                                isLoading={title ? false : true}
                            >
                                {title}
                            </PlayVideoButton>
                        ) : !isTvod && !isBookmared && !isComingSoonItem && item.programType === 'series' ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.VodeItem}
                                item={nextEpisode || item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="play-now-button"
                                isLoading={title ? false : true}
                            >
                                {title}
                            </PlayVideoButton>
                        ) : isLocked && !isTvod && !isComingSoonItem && item.programType === 'episode' ? (
                            <Button onClick={handleLockedPlayClick} data-test-id="overlay-button">
                                {isBookmared ? __('resume_watching') : __('play_now')}
                                <FontIcon name="lock" />
                            </Button>
                        ) : !isTvod && !isComingSoonItem && item.programType === 'episode' ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.EpisodeItem}
                                item={item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="overlay-button"
                                variant="button"
                            >
                                {isBookmared ? __('resume_watching') : __('play_now')}
                                <FontIcon name="play-outline" />
                            </PlayVideoButton>
                        ) : !isTvod && !isComingSoonItem && isBookmared && item.programType === 'movie' ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.TVodItem}
                                item={item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="resume-watching-button"
                            >
                                {isBookmared ? __('resume_watching') : __('play_now')}
                                <FontIcon name="play-outline" />
                            </PlayVideoButton>
                        ) : !isTvod && !isComingSoonItem && !isBookmared && item.programType === 'movie' ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.TVodItem}
                                item={item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="play-now-button"
                            >
                                {__('play_now')}
                                {isLocked ? <FontIcon name="lock" /> : <FontIcon name="play-outline" />}
                            </PlayVideoButton>
                        ) : isTvod && !isComingSoonItem && (!isRented || (hours <= 0 && minutes <= 0)) ? (
                            <Button
                                loading={isLoading}
                                color="orange"
                                onClick={(e) => {
                                    if (showGuestPromotionPopup(e)) {
                                        return;
                                    }
                                    handleGenerateOtpApi();
                                }}
                                data-test-id="rent-button"
                            >
                                <span dangerouslySetInnerHTML={{ __html: tvodRentNowBtnText }}></span>
                            </Button>
                        ) : isTvod && !isComingSoonItem ? (
                            <PlayVideoButton
                                buttonType={ButtonTypes.TVodItem}
                                item={item}
                                componentType={componentType}
                                componentName={componentName}
                                testId="play-now-button"
                            >
                                {__('play_now')}
                                <FontIcon name="play-outline" />
                            </PlayVideoButton>
                        ) : isTvod && !isComingSoonItem && (isRented || (hours > 0 && minutes > 0)) ? (
                            <>
                                {isBookmared ? (
                                    <PlayVideoButton
                                        buttonType={ButtonTypes.TVodItem}
                                        item={item}
                                        componentType={componentType}
                                        componentName={componentName}
                                        testId="continue-watching-button"
                                    >
                                        {isBookmared ? __('resume_watching') : __('play_now')}
                                        <FontIcon name="play-outline" />
                                    </PlayVideoButton>
                                ) : (
                                    <PlayVideoButton
                                        buttonType={ButtonTypes.TVodItem}
                                        item={item}
                                        componentType={componentType}
                                        componentName={componentName}
                                        testId="overlay-button"
                                    >
                                        {__('play_now')}
                                        <FontIcon name="play-outline" />
                                    </PlayVideoButton>
                                )}
                            </>
                        ) : null
                    }
                    watchlist={{
                        secondary: true,
                        children: [__('home_carousel_watch_list')],
                        onClick: (e) => {
                            if (showGuestPromotionPopup(e)) {
                                return;
                            }
                            handleToggleWatchlistItem(e);
                        },
                        // loading: toggleWatchlistMutation.isPending,
                        icon: isAdded ? 'close' : 'cross',
                    }}
                    trailer={
                        hasTrailerUrl
                            ? {
                                  social: true,
                                  secondary: true,
                                  children: __('watch_trailer'),
                                  onClick: handleShowTrailer,
                              }
                            : null
                    }
                    {...(percentageNumber != undefined ? { progress: `${percentageNumber}%` } : {})}
                    className="details"
                />
                <PlayTrailer
                    playerContainerRef={playerContainerRef}
                    showTrailer={showTrailer}
                    trailerResolved={item?.trailerMediaUrl}
                    closeTrailerPlayer={closeTrailerPlayer}
                    item={item}
                />
                {hasTrailerUrl && !isMobile && (
                    <FlexChild alignItems="end">
                        <Button small round secondary onClick={handleMute} data-test-id="mute-button">
                            <FontIcon name={isMuted ? 'mute' : 'volume-1'} size="smaller" md="smaller"></FontIcon>
                        </Button>
                    </FlexChild>
                )}
            </FlexWrapper>
            <DetailsStyle
                provider={[
                    h.getImagizerUrl(
                        promotionProviderImages?.[item.provider]?.imageUrl,
                        PROVIDER.WIDTH,
                        PROVIDER.HEIGHT,
                        PROVIDER.QUALITY
                    ),
                    PROVIDER.WIDTH,
                    PROVIDER.HEIGHT,
                ]}
                rating={item.ratings ? __(`rating_${item.ratings.toLowerCase()}`) : null}
                info={[
                    ...(item.genre && Array.isArray(item.genre) ? item.genre.map((g) => __(g)) : []),
                    item.duration > 0 ? `${f2.convertSecondsToMinutes(item.duration)} ${__('min')}` : null,
                    item.year ? `${item.year}` : null,
                    runtimeLength > 0 && f2.convertSecondsToMinutes(runtimeLength) + __('min'),
                    {
                        [__('movie_languages')]: Array.isArray(item?.language) ? item.language.map((l) => __(l)) : [],
                    },
                ].filter(Boolean)}
                paddingBottom={{ default: '2x', md: '3x' }}
            />
            <FlexWrapper direction="row" gap="6x" paddingBottom={{ default: '2x' }} className="reward-compact">
                {!isGuest && <LikeDislikeButton itemGuid={item?.guid || ''} item={item} />}
                {expiryDate && (hours > 0 || minutes > 0) && (
                    <RentTimer
                        {...(hours <= 0 &&
                        minutes < (h.getTVodConfig().diffExpirationTimeInMinutes as unknown as number)
                            ? { almostEnded: true }
                            : {})}
                        inline
                        timeCount={[hours > 0 ? hours : minutes, hours > 0 ? 'hour' : 'minute']}
                        timeType={
                            hours > 0 ? `${hours === 1 ? 'hour' : 'hours'}` : `${minutes === 1 ? 'minute' : 'minutes'}`
                        }
                        labelRemaining={'left'}
                    />
                )}
                {!isGuest && gamificationConfig?.enable && (
                    <RewardLabel
                        label={isTvod && !isRented ? __('rent_movies_earn_points') : __('watch_movies_earn_points')}
                        url={__('gamificationPointsImage')}
                    />
                )}
            </FlexWrapper>
            {!profile.selectedProfile?.kidsMode && (
                <>
                    <UpgradedBanner content={upgraded_banner_fields} />
                    <Separator theme="none" margin="1x" />
                </>
            )}
        </FlexWrapper>
    );
};
